import { useEffect, useMemo, useState } from "react";
import { Loader, Row, SelectPicker, Tooltip, Whisper } from "rsuite";
import {
  useAllocationFilters,
  useAllocationSummary,
} from "../../../../react-query/hooks/HVolumeAllocation";
import { ColumnGroup } from "primereact/columngroup";
import { Column } from "primereact/column";
import {
  IVendorVolumeAllocationSummaryObject,
  IVolumeAllocationSummaryObj,
} from "../../../../services/interfaces/IVolumeAllocation";
import {
  numberFormatter,
  numberFormatterInK,
} from "../../../../utils/formatter";
import DownloadIcon from "../../../../assets/download-green.svg";
import SortDefault from "../../../../assets/sort_default.svg";
import SortAsc from "../../../../assets/sort_asc.svg";
import SortDsc from "../../../../assets/sort_desc.svg";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { DataTable } from "primereact/datatable";
import useResponsiveWidth from "../../../../react-query/hooks/useResponsiveWidth";
import moment from "moment";
import ProductInfoIcon from "../../../../assets/info-circle-grey.svg";
import { appInsights } from "../../../../router/router";
import { sortCommodityFirst, getDefaultCommodity } from "../../../../utils/helpers/commodityOrder";
import { useAppSelector } from "../../../../services/redux/hooks/hooks";
const OverallAllocationSummary = () => {
  const width = useResponsiveWidth();
  const { filters, isLoading } = useAllocationFilters(
    localStorage.getItem("userEmail"),
  );
  const [commodity, setCommodity] = useState<null | string>(null);
  const disableScreens = useAppSelector((state) => {
    return state.disableScreens;
  });

  const commodityAccessMap =
    disableScreens?.allocationSummary &&
      typeof disableScreens.allocationSummary === "object" &&
      !Array.isArray(disableScreens.allocationSummary)
      ? (disableScreens.allocationSummary as Record<string, boolean>)
      : null;

  const allCommodityOptions = filters?.commodities || [];
  const allowedCommodityOptions = commodityAccessMap
    ? allCommodityOptions.filter(
      (item: { commodity: string }) => commodityAccessMap[item.commodity],
    )
    : allCommodityOptions;
  const disabledCommodityValues = commodityAccessMap
    ? allCommodityOptions
      .filter((item: { commodity: string }) => !commodityAccessMap[item.commodity])
      .map((item: { commodity: string }) => item.commodity)
    : [];

  const { data: volumeSummaryData, isLoading: summaryLoading } =
    useAllocationSummary(commodity, localStorage.getItem("userEmail"));

  // const [vendorList, setVendorList] = useState
  const [vendorList, setVendorList] = useState<string[] | null>(null);

  const [sortColumn, setSortColumn] = useState("");
  const [sortType, setSortType] = useState();
  const [tableData, setTableData] = useState<any>([]);

  useEffect(() => {
    if (allowedCommodityOptions.length > 0 && commodity === null) {
      setCommodity(getDefaultCommodity(allowedCommodityOptions) ?? null);
      return;
    }

    if (
      commodity !== null &&
      allowedCommodityOptions.length > 0 &&
      !allowedCommodityOptions.some(
        (item: { commodity: string }) => item.commodity === commodity,
      )
    ) {
      setCommodity(getDefaultCommodity(allowedCommodityOptions) ?? null);
      return;
    }

    if (commodity !== null && allowedCommodityOptions.length === 0) {
      setCommodity(null);
    }
  }, [allowedCommodityOptions, commodity]);

  const getVendorCountPerDistrict = (
    districtList: IVolumeAllocationSummaryObj[],
  ) => {
    let vendorList: string[] = [];

    districtList.map((districtData) => {
      Object.keys(districtData)
        .filter(
          (distProp) =>
            distProp !== "district" &&
            distProp !== "incremental_spend" &&
            distProp !== "forecast",
        )
        .map((vendor) =>
          !vendorList.includes(vendor) ? vendorList.push(vendor) : vendorList,
        );
    });
    return vendorList;
  };

  // const vendorList = getVendorCountPerDistrict(volumeSummaryData.data);

  const convertToSingleObject = (data: any) => {
    const result: any = {
      district: data.district,
      inc_spend: data.incremental_spend,
    };

    // Loop through the keys in the data object
    for (const key in data) {
      if (typeof data[key] === "object") {
        // Add dynamic keys for recommended and final allocations
        result[`${key.toLowerCase()}_rec_allocation`] = data[key]
          ?.recommended_allocations
          ? data[key].recommended_allocations
          : 0;
        result[`${key.toLowerCase()}_final_allocation`] = data[key]
          ?.final_allocations
          ? data[key].final_allocations
          : 0;
      }
    }

    return result;
  };

  useEffect(() => {
    if (volumeSummaryData && Object.keys(volumeSummaryData).length > 0) {
      setVendorList(getVendorCountPerDistrict(volumeSummaryData.data));
    }
  }, [volumeSummaryData?.data]);

  // let formattedTableData = volumeSummaryData?.data?.map((item: any) => {
  //   return convertToSingleObject(item);
  // });
  const formattedTableData = useMemo(() => {
    return (
      volumeSummaryData?.data?.map((item: any) =>
        convertToSingleObject(item),
      ) ?? []
    );
  }, [volumeSummaryData]);
  useEffect(() => {
    setTableData(formattedTableData);
  }, [formattedTableData]);
  const RecommendedAllocationHeader = () => {
    const unit = commodity?.toLowerCase() === "copper wire" ? "Lbs" : "TLs";
    return (
      <div className="flex justify-center">
        Calculated Allocation Recommendations ({unit})
      </div>
    );
  };

  const FinalAllocationHeader = () => {
    const unit = commodity?.toLowerCase() === "copper wire" ? "Lbs" : "TLs";
    return (
      <div className="flex justify-center">
        <span className="invisible">abc</span>Final Allocation Recommendations
        ({unit})
        <span className="invisible">abc</span>
      </div>
    );
  };

  const OverpaidAmountHeader = () => {
    return (
      <div
        className="w-full flex justify-between items-center cursor-pointer"
        onClick={() => handleSort("forecast", sortType)}
      >
        <div className="flex flex-col justify-start items-start">
          <span>
            Incremental Spend Amount
            <Whisper
              className="inline"
              placement="top"
              speaker={
                <Tooltip placement="auto" className="max-w-[18vw]">
                  The incremental spend of the custom allocation compared to the
                  recommended allocation.
                </Tooltip>
              }
            >
              {" "}
              <img
                src={ProductInfoIcon}
                alt="info"
                className="w-[1.75vh] cursor-pointer inline"
              ></img>
            </Whisper>
          </span>
        </div>{" "}
        <div>{renderSortIcon("forecast")}</div>
      </div>
    );
  };

  const VendorHeader = (vendor: string) => {
    return (
      <div className="flex flex-col items-start justify-start">{vendor}</div>
    );
  };

  const VendorRecommendedBody = (rowdata: any, vendor: string) => {
    const data: IVendorVolumeAllocationSummaryObject | string | null | number =
      rowdata[vendor];
    const value = rowdata[`${vendor?.toLowerCase()}_rec_allocation`];
    return (
      <div className="flex justify-start w-full">
        {value
          ? commodity?.toLowerCase() === "copper wire"
            ? numberFormatterInK(value, 0)
            : value
          : "-"}
      </div>
    );
  };
  const VendorFinalBody = (rowdata: any, vendor: string) => {
    const data: IVendorVolumeAllocationSummaryObject | string | null | number =
      rowdata[vendor];
    const value = rowdata[`${vendor?.toLowerCase()}_final_allocation`];
    return (
      <div className="flex justify-start w-full">
        {value
          ? commodity?.toLowerCase() === "copper wire"
            ? numberFormatterInK(value, 0)
            : value
          : "-"}
      </div>
    );
  };

  const HeaderGroup = (
    <ColumnGroup>
      <Row>
        <Column header={""} frozen colSpan={2} />
        <Column
          header={RecommendedAllocationHeader}
          colSpan={vendorList?.length}
          headerStyle={{
            borderLeft: "2px solid #86BC25",
            borderRight: "2px solid #86BC25",
          }}
        />
        <Column
          header={FinalAllocationHeader}
          colSpan={vendorList?.length}
          headerStyle={{
            borderLeft: "2px solid #86BC25",
          }}
        />
      </Row>
      <Row>
        <Column
          field="district"
          header={() => (
            <div
              className="w-full flex justify-between items-center cursor-pointer"
              onClick={() => handleSort("district", sortType)}
            >
              <div>District</div> <div>{renderSortIcon("district")}</div>
            </div>
          )}
          frozen
        />
        <Column field="forecast" header={OverpaidAmountHeader} frozen />
        {vendorList?.map((vendor: string, index: number) => {
          return (
            <Column
              key={vendor}
              header={() => VendorHeader(vendor)}
              sortable
              sortField={`${vendor?.toLowerCase()}_rec_allocation`}
              headerStyle={
                index === 0 || index === vendorList.length - 1
                  ? index === 0
                    ? {
                        borderLeft: "2px solid #86BC25",
                      }
                    : {
                        borderRight: "2px solid #86BC25",
                      }
                  : {}
              }
            ></Column>
          );
        })}
        {vendorList?.map((vendor: string, index: number) => {
          return (
            <Column
              key={vendor}
              header={() => VendorHeader(vendor)}
              sortField={`${vendor?.toLowerCase()}_final_allocation`}
              sortable
              headerStyle={
                index === 0
                  ? {
                      borderLeft: "2px solid #86BC25",
                    }
                  : {}
              }
            ></Column>
          );
        })}
      </Row>
    </ColumnGroup>
  );

  const handleDownloadExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Allocation Summary");
    const unit = commodity?.toLowerCase() === "copper wire" ? "Lbs" : "TLs";
    const baseHeaders = [
      { header: "Commodity", key: "commodity", width: 10 },
      { header: "District", key: "district", width: 10 },
      { header: `Projected Releases (${unit})`, key: "forecast", width: 10 },
      {
        header: "Incremental Spend Amount",
        key: "incremental_spend",
        width: 10,
        style: { numFmt: "$#,##0.00" },
      },
      {
        header: "Allocations and PO Cost",
        key: "allocations_and_po_cost",
        width: 10,
      },
    ];

    let vendors =
      volumeSummaryData?.vendor_list.filter((ele) => ele ?? false) ?? [];

    let vendorHeaders: any = [];
    vendors?.map((item: string) => {
      let obj1 = {
        header: item,
        // key: `${item}_final_allocations`,
        key: item,
        width: 20,
      };
      // let obj2 = {
      //   header: item,
      //   key: `${item}_latest_quote`,
      //   width: 10,
      //   style: { numFmt: "$#,##0.00" },
      // };

      vendorHeaders?.push(obj1);
      // vendorHeaders?.push(obj2);
    });

    worksheet.columns = [...baseHeaders, ...vendorHeaders];

    // let SecondRow: any = [null, null, null, null];

    // vendors?.map((item: string) => {
    //   SecondRow?.push("Allocated  Volume (TLs)");
    //   SecondRow?.push(
    //     `Final Quote ($/cft)${
    //       commodity === "Steel Conduit" ? " '3/4 EMT" : ' 4" PVC 40'
    //     }`,
    //   );
    // });

    // worksheet.addRow(SecondRow);

    volumeSummaryData?.data?.map((item: any, i: number) => {
      const start = 2;
      const end = 3;
      let row1: any = {
        // commodity: commodity,
        district: item?.district,
        forecast: item?.forecast,
        incremental_spend: item?.incremental_spend
          ? item?.incremental_spend
          : null,
        allocations_and_po_cost: `Allocated Volume (${unit})`,
        ...vendors.reduce((acc, vendor) => {
          return {
            ...acc,
            [vendor]: item?.[vendor]?.final_allocations ?? null,
          };
        }, {}),
      };

      let row2: any = {
        // commodity: commodity,
        allocations_and_po_cost: `PO Cost ($/cft) ${
          commodity === "Steel Conduit"
            ? " '3/4 EMT"
            : commodity === "PVC Conduit"
              ? ' 4" PVC 40'
              : ""
        }`,
        ...vendors.reduce((acc, vendor) => {
          return {
            ...acc,
            [vendor]: item?.[vendor]?.latest_quote ?? null,
          };
        }, {}),
      };

      // vendors?.map((vendor: any) => {
      //   row1[vendor] = item[vendor]?.final_allocations
      //     ? item[vendor]?.final_allocations
      //     : null;
      //   row2[vendor] = item[vendor]?.latest_quote
      //     ? item[vendor]?.latest_quote
      //     : null;
      // });

      // vendors?.map((vendor: any) => {
      //   row[`${vendor}_final_allocations`] = item[vendor]?.final_allocations
      //     ? item[vendor]?.final_allocations
      //     : null;
      //   row[`${vendor}_latest_quote`] = item[vendor]?.latest_quote
      //     ? item[vendor]?.latest_quote
      //     : null;
      // });

      worksheet.addRow(row1);
      worksheet.addRow(row2);
      worksheet.mergeCells(`B${start + i * 2}:B${end + i * 2}`);
      worksheet.mergeCells(`C${start + i * 2}:C${end + i * 2}`);
      worksheet.mergeCells(`D${start + i * 2}:D${end + i * 2}`);
    });

    worksheet.mergeCells(`A2:A${1 + volumeSummaryData?.data.length * 2}`);
    worksheet.getCell("A2").value = commodity;

    const headerRow = worksheet.getRow(1);
    // const headerRow2 = worksheet.getRow(2);
    headerRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.alignment = {
        wrapText: true,
        vertical: "middle",
        horizontal: "center",
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      cell.font = { bold: true };
    });

    // headerRow2.eachCell({ includeEmpty: true }, (cell) => {
    //   cell.alignment = {
    //     wrapText: true,
    //     vertical: "middle",
    //     horizontal: "center",
    //   };
    //   cell.border = {
    //     top: { style: "thin" },
    //     left: { style: "thin" },
    //     bottom: { style: "thin" },
    //     right: { style: "thin" },
    //   };
    //   cell.font = { bold: true };
    // });

    headerRow.height = 30;
    // headerRow2.height = 30;

    // Apply styling to all data rows
    worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
      if (rowNumber > 1) {
        row.eachCell({ includeEmpty: true }, (cell, cellNumber) => {
          cell.alignment = {
            wrapText: true,
            vertical: "middle",
            horizontal:
              cellNumber === 1 || cellNumber === 2 || cellNumber === 5
                ? "center"
                : "right",
          };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
          const text = row.getCell(5).value;
          for (let i = 6; i <= 6 + vendors?.length - 1; i++) {
            if (
              text &&
              typeof text === "string" &&
              text.trim() !== `Allocated Volume (${unit})`
            ) {
              row.getCell(i).numFmt = "$#,##0.00";
            }
          }
        });
      }
    });

    worksheet.columns.forEach((column: any, colNumber) => {
      if (colNumber <= 5) {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell: any) => {
          if (cell.value) {
            const cellValue = cell.value.toString();
            maxLength = Math.max(maxLength, cellValue.length);
          }
        });
        column.width = maxLength + 2; // Add some padding to the width
      }
    });

    // worksheet.getColumn(1).alignment = {
    //   wrapText: true,
    //   vertical: "middle",
    //   horizontal: "center",
    // };
    // worksheet.getColumn(5).alignment = {
    //   wrapText: true,
    //   vertical: "middle",
    //   horizontal: "center",
    // };

    worksheet.getColumn(4).numFmt = "$#,##0.00";
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Allocation Summary_${commodity}.xlsx`);
    appInsights.trackEvent({
      name: "Allocation Summary: Overall Summary",
      properties: {
        description: `${localStorage.getItem(
          "userEmail",
        )} has downloaded "Allocation Summary_${commodity}.xlsx" Report`,
      },
    });
  };

  const sortByColumnName = (
    data: any[],
    order: "asc" | "desc" | null,
    column: string,
  ) => {
    if (!order) return data;
    return [...data].sort((a, b) => {
      if (a[column] < b[column]) return order === "asc" ? -1 : 1;
      if (a[column] > b[column]) return order === "asc" ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (columnName: string, sortType: any) => {
    const order: any = sortType ? (sortType === "asc" ? "desc" : null) : "asc";
    setSortColumn(columnName);
    setSortType(order);
    if (order) {
      setTableData(sortByColumnName(formattedTableData, order, columnName));
    } else {
      setTableData(formattedTableData); // original order
    }
  };

  const renderSortIcon = (field: any) => {
    const iconSize = 12;
    if (sortColumn === field && sortType === "asc") {
      return <img width={iconSize} height={iconSize} src={SortAsc} alt="asc" />;
    } else if (sortColumn === field && sortType === "desc") {
      return (
        <img width={iconSize} height={iconSize} src={SortDsc} alt="desc" />
      );
    } else {
      return (
        <img
          width={iconSize}
          height={iconSize}
          src={SortDefault}
          alt="default"
        />
      );
    }
  };

  return (
    <>
      {isLoading || summaryLoading ? (
        <Loader
          backdrop
          content="Loading..."
          size="md"
          vertical
          className="text-black font-bold"
          style={{ opacity: 0.6 }}
        />
      ) : (
        <div className="flex flex-col justify-stretch items-stretch h-full volume-allocation">
          <div className="grow shrink-0 basis-[17%] ps-[3%] pr-[5%] pt-[1.6%] flex flex-col flex-nowrap justify-start items-start gap-0 shadow-normal relative">
            <div className="flex justify-between items-center w-full">
              <div className="font-normal text-[2vw]">Allocation Summary</div>
              <div className="flex justify-start items-center gap-8">
                <div className="flex flex-col justify-start items-start">
                  <label className="font-bold text-[0.8vw]">Commodity</label>
                  <SelectPicker
                    data={sortCommodityFirst(allCommodityOptions)}
                    value={commodity ?? undefined}
                    labelKey="commodity"
                    valueKey="commodity"
                    style={{ width: width }}
                    disabledItemValues={disabledCommodityValues}
                    onChange={(value: string | null) => {
                      if (value && !disabledCommodityValues.includes(value)) {
                        setCommodity(value);
                      }
                    }}
                    placeholder="All"
                    cleanable={false}
                  ></SelectPicker>
                </div>
              </div>
            </div>
            <span className="font-bold text-fs16 text-[#858585]">
              {commodity === "Copper Wire"
                ? "Negotiation Week: "
                : "Negotiation Month: "}
              <span className="font-bold underline text-deloitteGreen">
                {commodity && filters?.monthYear?.[commodity]
                  ? commodity === "Copper Wire"
                    ? moment.utc(filters.monthYear[commodity]).format("DD MMMM YYYY")
                    : moment.utc(filters.monthYear[commodity]).format("MMMM YYYY")
                  : ""}
              </span>
            </span>
          </div>
          <div className="bg-[#F1F1F1] grow shrink p-[2%] pr-[5%] flex flex-col flex-nowrap justify-start items-start gap-4 h-full w-full">
            {allowedCommodityOptions.length === 0 ? (
              <div className="w-full h-full flex justify-center items-center bg-white shadow-module-card border border-[#bebebe] rounded-md">
                <span className="font-semibold text-fs16 text-[#858585]">
                  You do not have access to Allocation Summary for any commodity.
                </span>
              </div>
            ) : (
              <>
            <div className="w-full basis-[7vh] bg-white font-bold p-pd13 shadow-module-card border border-[#bebebe]  rounded-md flex flex-row justify-between items-center flex-nowrap">
              <div className="flex items-center">
                <div className="mr-[1vh] flex">
                  <span className="text-fs16 mr-[1vh]">
                    Total Incremental Spend Amount
                  </span>
                  <div
                    onClick={() => handleDownloadExcel()}
                    className="cursor-pointer flex items-center"
                  >
                    <img src={DownloadIcon} className="w-[1vw] h-[1vw]" />
                  </div>
                </div>
              </div>

              <span className="text-fs24 pr-5">
                {numberFormatterInK(
                  volumeSummaryData.total_overpaid_amount,
                  1,
                  "$",
                )}
              </span>
            </div>
            <div
              className="w-full allocation-summary-table"
              style={{ maxWidth: "80vw" }}
            >
              <DataTable
                value={tableData ?? []}
                removableSort
                headerColumnGroup={HeaderGroup}
                stripedRows
                scrollable
                scrollHeight="65vh"
              >
                <Column
                  field="district"
                  header="District"
                  style={{ minWidth: "7vw" }}
                  frozen /* alignFrozen="left" */
                />
                <Column
                  field="forecast"
                  header={OverpaidAmountHeader}
                  style={{ minWidth: "7vw" }}
                  frozen
                  bodyStyle={{ borderRight: "2px solid #86BC25" }}
                  /* alignFrozen="left" */ body={(rowdata) => {
                    return (
                      <span>
                        {rowdata?.inc_spend
                          ? numberFormatterInK(rowdata.inc_spend, 1, "$")
                          : "-"}
                      </span>
                    );
                  }}
                />
                {vendorList?.map((vendor: string, index: number) => {
                  return (
                    <Column
                      key={vendor}
                      field={vendor}
                      // style={{ minWidth: "5vw" }}
                      body={(rowdata) => VendorRecommendedBody(rowdata, vendor)}
                      bodyStyle={
                        index === 0 || index === vendorList.length - 1
                          ? index === 0
                            ? {
                                borderLeft: "2px solid #86BC25",
                              }
                            : {
                                borderRight: "2px solid #86BC25",
                              }
                          : {}
                      }
                    ></Column>
                  );
                })}
                {vendorList?.map((vendor: string, index: number) => {
                  return (
                    <Column
                      key={vendor}
                      field={vendor}
                      // style={{ minWidth: "5vw" }}
                      body={(rowdata) => VendorFinalBody(rowdata, vendor)}
                      bodyStyle={
                        index === 0
                          ? {
                              borderLeft: "2px solid #86BC25",
                            }
                          : {}
                      }
                    ></Column>
                  );
                })}
              </DataTable>
            </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default OverallAllocationSummary;
